import { Navigate, Link } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';
import { fmtCents, proposalStatusColor } from './AlphaOmegaDashboardPage';

// ── Types ────────────────────────────────────────────────────────────────────

export interface SpawnProposalResponse {
  proposal_id: string;
  company_id: string;
  title: string;
  description: string | null;
  budget_cents: number;
  status: string;
  created_at: string;
}

interface SpawnProposalsResponse {
  proposals: SpawnProposalResponse[];
  total: number;
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function AlphaOmegaSpawnProposalsPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [statusFilter, setStatusFilter] = useState('pending');

  const { data, isPending, isError } = useQuery({
    queryKey: ['alpha-omega', 'spawn-proposals', statusFilter],
    queryFn: () => apiFetch<SpawnProposalsResponse>(
      statusFilter === 'all'
        ? '/alpha-omega/spawn-proposals'
        : `/alpha-omega/spawn-proposals?status=${statusFilter}`
    ),
    refetchInterval: 30_000,
  });

  const filters = ['pending', 'approved', 'rejected', 'all'];

  const filterBtnStyle = (active: boolean): React.CSSProperties => ({
    background: active ? 'rgba(125,211,252,0.12)' : 'rgba(255,255,255,0.04)',
    border: active ? '1px solid rgba(125,211,252,0.4)' : '1px solid rgba(255,255,255,0.1)',
    borderRadius: 4, color: active ? '#7dd3fc' : 'var(--omega-ink)',
    fontFamily: 'var(--omega-mono)', fontSize: '8pt', fontWeight: 600,
    padding: '4px 10px', cursor: 'pointer', letterSpacing: '0.04em',
  });

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA',       to: '/omega' },
        { label: 'Alpha-Omega', to: '/omega/alpha-omega' },
        { label: 'Proposals Spawn' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Spawn Proposals
        </h1>
        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          {filters.map(f => (
            <button key={f} style={filterBtnStyle(statusFilter === f)} onClick={() => setStatusFilter(f)}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError   && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading proposals.</div>}

      {data && (
        <>
          <div style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)', marginBottom: 12 }}>
            {data.total} Proposals
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 800 }}>
            {data.proposals.map(p => (
              <Link
                key={p.proposal_id}
                to={`/omega/alpha-omega/spawn-proposals/${p.proposal_id}`}
                style={{
                  display: 'grid', gridTemplateColumns: '1fr 120px 90px 90px',
                  gap: 12, padding: '10px 14px',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
                  borderRadius: 4, textDecoration: 'none', color: 'var(--omega-ink)',
                }}
              >
                <span style={{ fontSize: '9pt', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</span>
                <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.company_id.slice(0, 12)}</span>
                <span style={{ fontSize: '8.5pt', fontFamily: 'var(--omega-mono)' }}>{fmtCents(p.budget_cents)}</span>
                <span style={{ fontSize: '8.5pt', color: proposalStatusColor(p.status), fontFamily: 'var(--omega-mono)', textTransform: 'uppercase' }}>{p.status}</span>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
